"use client";

import { useSpaceSegmentScroller } from "@/app/_hooks/Timeline/useSpaceSegmentScroller";
import { scrollToBooking, scrollToDate } from "@/app/_utils/scrolling";
import { differenceInCalendarDays, startOfDay } from "date-fns";
import { createContext, useCallback, useContext, useRef } from "react";
import { useSelector } from "react-redux";

export const TimelineScrollContext = createContext();

export const TimelineScrollProvider = ({ children }) => {
  const timelineRef = useRef(null);

  const timelineStart = useSelector(
    (state) => state.timelineSettings.timelineStart
  );
  const visibleDays = useSelector(
    (state) => state.timelineSettings.visibleDays
  );

  const toBooking = useCallback(
    (booking, { behavior = "smooth", focusDate } = {}) => {
      const container = timelineRef.current;
      if (!container || !booking) return;
      try {
        container.dataset.orchestratingScroll = "1";
      } catch {}

      let targetLeft = container.scrollLeft;
      let targetTop = container.scrollTop;
      try {
        const colWidth = container.clientWidth / Math.max(1, visibleDays);
        const start = startOfDay(new Date(timelineStart));
        const startDate = startOfDay(new Date(focusDate ?? booking.StartDate));
        const dayIndex = Math.max(0, differenceInCalendarDays(startDate, start));
        targetLeft = Math.max(0, dayIndex * colWidth);

        const el = container.querySelector(
          `[data-booking-bar="${booking.AutoID}"]`
        );
        if (el && el.getBoundingClientRect) {
          const cRect = container.getBoundingClientRect();
          const eRect = el.getBoundingClientRect();
          const rawTop = eRect.top - cRect.top + container.scrollTop;
          const baseline = Math.max(0, rawTop - 60);
          const maxTop = Math.max(
            0,
            container.scrollHeight - container.clientHeight
          );
          targetTop = Math.max(0, Math.min(maxTop, baseline));
        }
      } catch {}

      scrollToBooking({
        booking,
        scrollRef: timelineRef,
        timelineStart,
        visibleDays,
        behavior,
        focusDate,
      });

      const startedAt = Date.now();
      const timeoutMs = behavior === "smooth" ? 900 : 50;
      const watch = () => {
        const el = timelineRef.current;
        if (!el) return;
        const atTarget =
          Math.abs(el.scrollLeft - targetLeft) <= 1 &&
          Math.abs(el.scrollTop - targetTop) <= 1;
        if (atTarget || Date.now() - startedAt > timeoutMs) {
          try {
            el.dataset.orchestratingScroll = "0";
          } catch {}
          return;
        }
        requestAnimationFrame(watch);
      };
      requestAnimationFrame(watch);
    },
    [timelineStart, visibleDays]
  );

  const toDate = useCallback(
    (date, { behavior = "smooth" } = {}) => {
      const container = timelineRef.current;
      if (!container || !date) return;

      const safeVisibleDays = Math.max(1, Number(visibleDays) || 1);
      const colWidth = container.clientWidth / safeVisibleDays;
      const lockTop = container.scrollTop;

      const normalizeMidnight = (value) => {
        if (!value) return null;
        const next = new Date(value);
        if (Number.isNaN(next.getTime())) return null;
        next.setHours(0, 0, 0, 0);
        return next;
      };

      const base = normalizeMidnight(timelineStart);
      const targetDate = normalizeMidnight(date);
      if (!base || !targetDate) return;

      const dayIndex = Math.max(0, differenceInCalendarDays(targetDate, base));
      const targetLeft = dayIndex * colWidth;

      try {
        container.dataset.orchestratingScroll = "1";
      } catch {}

      const clearFlag = () => {
        const el = timelineRef.current;
        if (!el) return;
        try {
          el.dataset.orchestratingScroll = "0";
        } catch {}
      };

      const enforceHorizontalOnly = () => {
        const el = timelineRef.current;
        if (!el) return clearFlag();
        if (Math.abs(el.scrollTop - lockTop) > 0.5) {
          el.scrollTop = lockTop;
        }
      };

      scrollToDate({
        container,
        date: targetDate,
        timelineStart: base,
        colWidth,
        behavior,
      });

      if (behavior === "smooth") {
        const startedAt = Date.now();
        const watch = () => {
          const el = timelineRef.current;
          if (!el) return clearFlag();
          enforceHorizontalOnly();
          const done =
            Math.abs(el.scrollLeft - targetLeft) <= 1 ||
            Date.now() - startedAt > 900;
          if (done) {
            enforceHorizontalOnly();
            return clearFlag();
          }
          requestAnimationFrame(watch);
        };
        requestAnimationFrame(watch);
      } else {
        enforceHorizontalOnly();
        clearFlag();
      }
    },
    [timelineStart, visibleDays]
  );

  const toSpaceSegment = useSpaceSegmentScroller({
    timelineRef,
    timelineStart,
    visibleDays,
  });

  return (
    <TimelineScrollContext.Provider
      value={{
        timelineRef,
        scroll: {
          toBooking,
          toDate,
          toSpaceSegment,
        },
      }}
    >
      {children}
    </TimelineScrollContext.Provider>
  );
};

export const useTimelineScroll = () => {
  return useContext(TimelineScrollContext);
};
